import { useLocalAuth } from '@/services/auth';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { Alert, Box, CircularProgress, styled, TextField } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TitleText } from '@/components/TitleText';
import { RoundedButton } from '@/components/RoundedButton';
import { AuthBox, AuthBoxContainer } from '@/components/AuthBox';
import { consumeSessionExpiredNotice } from '@/services/auth/sessionExpiredNotice';

const LocalSignInButton = styled(RoundedButton)({
  marginTop: '18px',
  height: '54px',
});

const LocalSignInPage = () => {
  const { login, isAuthenticated } = useLocalAuth();
  const { data: user } = useGetCurrentUser({ skipRedirect: true });
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ userId: '' });
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [wasSessionExpired] = useState(consumeSessionExpiredNotice);

  useEffect(() => {
    const isAuthorized = user?.user?.settings?.some(tenant => tenant.userEnabled) ?? false;
    if (isAuthenticated && isAuthorized) {
      navigate('/tenants');
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(previous => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleLocalSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setIsLoading(true);

    try {
      localStorage.setItem('userId', formData.userId);
      await login(formData);
    } catch (error) {
      setError(error instanceof AxiosError ? error.response?.data.message : 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthBoxContainer>
      <AuthBox>
        <TitleText>Sign In</TitleText>
        {wasSessionExpired && <Alert severity='warning'>Your session expired. Please sign in again.</Alert>}
        {error && <Alert severity='error'>{error}</Alert>}
        <Box component='form' onSubmit={event => void handleLocalSignIn(event)}>
          <TextField
            slotProps={{
              input: {
                sx: { borderRadius: '12px' },
              },
            }}
            margin='normal'
            name='userId'
            label='User ID'
            required
            fullWidth
            autoComplete='userId'
            value={formData.userId}
            onChange={handleChange}
            disabled={isLoading}
          />
          <LocalSignInButton type='submit' fullWidth variant='contained' disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Sign In'}
          </LocalSignInButton>
        </Box>
      </AuthBox>
    </AuthBoxContainer>
  );
};

export default LocalSignInPage;
